// src/components/editor/CommenterCredentials.jsx
//
// Right-rail contents for the Collab tab: who wrote the selected comment and
// what standing they have. Replaces the default rail (chapter progress / theme
// / reviewer activity) only while a comment is selected.
//
// Fields with no backing data render "Coming soon", matching the convention
// AnalyticsPanel already established for the author analytics tab — the API
// returns null and the UI says so rather than inventing a number. Ranking
// score, specialties, and NDA status all need their own modelling work.
//
// Skinned purple/gold like the rest of the rail; the mock's cream palette is
// treated as layout only.
import React, { useEffect, useState } from 'react';
import { Box, Typography, Avatar, CircularProgress } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { getJSON } from '../../utils/api';

const GOLD = '#D4AF37';
const COMING_SOON = 'Coming soon';

const nameOf = (u) =>
  u?.username || u?.name || (u?.email ? String(u.email).split('@')[0] : 'User');

const initialsOf = (u) => {
  const parts = String(nameOf(u)).trim().split(/\s+/);
  const a = parts[0]?.[0] || '';
  const b = parts.length > 1 ? parts[parts.length - 1][0] : (parts[0]?.[1] || '');
  return (a + b).toUpperCase();
};

// Turn the stored role slugs into something a person would say out loud.
const ROLE_LABELS = {
  feedback_reviewer: 'Developmental reviewer',
  reviewer: 'Reviewer',
  author: 'Author',
  designer: 'Cover designer',
  design: 'Cover designer',
  editor: 'Editor',
  edit: 'Editor',
  viewer: 'Viewer',
  view_only: 'Viewer',
  reader: 'Reader',
  admin: 'Administrator',
};

const roleLabel = (roles) => {
  const list = Array.isArray(roles) ? roles : [];
  // "reader" is the default every account carries, so it's only meaningful
  // when it's the ONLY thing they are.
  const meaningful = list.filter((r) => r !== 'reader');
  const pick = (meaningful[0] || list[0] || '').toLowerCase();
  return ROLE_LABELS[pick] || (pick ? pick.replace(/_/g, ' ') : 'Collaborator');
};

const SectionLabel = ({ children }) => (
  <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', mb: 1 }}>
    {children}
  </Typography>
);

// Consistent treatment for a value we can't compute yet.
const Pending = () => (
  <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', fontStyle: 'italic', mb: 2.5 }}>
    {COMING_SOON}
  </Typography>
);

export default function CommenterCredentials({ comment }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);

  const author = comment?.user || null;
  const userId = author?.id || author?._id || null;

  useEffect(() => {
    if (!userId) { setProfile(null); return; }
    let alive = true;
    setLoading(true);
    getJSON(`/api/users/${userId}/reviewer-profile`)
      .then((data) => { if (alive) setProfile(data || null); })
      .catch(() => { if (alive) setProfile(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [userId]);

  if (!comment) {
    return (
      <Box className="editor-right-rail">
        <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', lineHeight: 1.6 }}>
          Select a comment to see who wrote it.
        </Typography>
      </Box>
    );
  }

  const acceptRate = profile?.reviewAcceptRate;
  const hasAcceptRate = acceptRate !== null && acceptRate !== undefined;

  return (
    // is-rail-open because this panel only renders once a comment is selected —
    // it is always something the user asked for. Below 900px the rail is an
    // overlay that stays off-screen without that class.
    <Box className="editor-right-rail is-rail-open">
      {/* Identity — available immediately from the comment itself, so it shows
          before (and even without) the profile request resolving. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2.5 }}>
        <Avatar sx={{ width: 38, height: 38, fontSize: 12, fontWeight: 700, bgcolor: '#6B4FA0' }}>
          {initialsOf(author)}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {nameOf(author)}
          </Typography>
          <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>
            {loading ? '…' : roleLabel(profile?.roles)}
          </Typography>
        </Box>
      </Box>

      <SectionLabel>Ranking score</SectionLabel>
      <Pending />

      {/* Deliberately labelled "Review accept rate", not "Accept rate": it
          counts draft approve/reject decisions, NOT how often this person's
          comments were accepted. Sitting beside Notify/Discard, the shorter
          label would read as the wrong metric. */}
      <SectionLabel>Review accept rate</SectionLabel>
      {loading ? (
        <CircularProgress size={14} sx={{ color: GOLD, mb: 2.5 }} />
      ) : hasAcceptRate ? (
        <>
          <Box sx={{ height: 7, borderRadius: '4px', background: 'rgba(255,255,255,0.15)', mb: 0.75, overflow: 'hidden' }}>
            <Box sx={{ height: '100%', width: `${Math.round(acceptRate * 100)}%`, background: GOLD, borderRadius: '4px' }} />
          </Box>
          <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', mb: 2.5 }}>
            {Math.round(acceptRate * 100)}%
            {profile?.reviewsCompleted ? ` of ${profile.reviewsCompleted} review${profile.reviewsCompleted === 1 ? '' : 's'}` : ''}
          </Typography>
        </>
      ) : (
        <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', fontStyle: 'italic', mb: 2.5 }}>
          No reviews yet
        </Typography>
      )}

      <SectionLabel>Specialties</SectionLabel>
      <Pending />

      <SectionLabel>NDA status</SectionLabel>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <LockOutlinedIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.45)' }} />
        <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', fontStyle: 'italic' }}>
          {COMING_SOON}
        </Typography>
      </Box>
    </Box>
  );
}
